import type { Prop } from '../../defs.ts'
import type { District } from '../plot.ts'
import { decals, look } from '../kit.ts'

const WALL = '#f4f1ea'
const TILE = '#3d4250'
const house = (x: number, y: number, w: number, d: number, extra: Partial<Prop> = {}): Prop => ({ kind: 'building', x, y, w, d, style: 'canal', color: WALL, roof: TILE, ...extra })

/**
 * Lantern Canals: a Jiangnan water town in the spirit of Hangzhou and Suzhou.
 * White walls and dark tile roofs on stone banks, arched bridges, willows,
 * red lanterns, and boats drifting through to the sea.
 */
export const canals: District = {
  id: 'canals',
  name: 'Lantern Canals',
  ground: 'cobble',
  ambience: ['sparkles'],
  build(p) {
    p.zone(0, 26, 36, 6, 'sea')
    p.zone(16, 0, 3, 26, 'canal')
    p.zone(0, 9, 36, 2, 'canal'); p.zone(0, 19, 36, 2, 'canal'); p.zone(6, 0, 2, 26, 'canal')
    for (const y of [4, 13, 23]) { p.zone(16, y, 3, 2, 'bridge'); p.zone(6, y, 2, 2, 'bridge') }
    for (const x of [3, 12, 24, 31]) { p.zone(x, 9, 2, 2, 'bridge'); p.zone(x, 19, 2, 2, 'bridge') }
    p.prop(
      house(0, 1, 5, 3), house(1, 5, 4, 3), house(9, 1, 6, 3), house(10, 5, 5, 3),
      house(20, 1, 5, 3), house(27, 1, 6, 3), house(20, 5, 4, 3), house(28, 5, 6, 3),
      house(0, 12, 5, 3), house(9, 11, 6, 3), house(9, 15, 6, 3), house(31, 11, 4, 4),
      house(21, 11, 8, 5, { door: 4, text: 'Lotus Teahouse', color: '#f6efe2', enter: 'lotus' }),
      house(0, 21, 5, 3), house(9, 21, 6, 3),
      house(21, 21, 4, 3, { style: 'shop', text: 'Dumplings', color: '#fff3e0', roof: '#b03a2e' }),
      house(27, 21, 4, 3, { style: 'shop', text: 'Lanterns', color: '#fff3e0', roof: '#b03a2e' }),
      { kind: 'gate', x: 35, y: 15, w: 1, d: 4, variant: 'paifang', text: '水乡' },
      { kind: 'boat', x: 27, y: 9 }, { kind: 'boat', x: 9, y: 19 }, { kind: 'boat', x: 33, y: 19 },
    )
    for (const [x, y] of [[14, 8], [20, 8], [26, 8], [34, 8], [2, 18], [14, 18], [30, 18], [14, 25], [26, 25], [2, 8]] as const) p.prop({ kind: 'lanternpost', x, y })
    p.tram(32, 24)
    p.place({ id: 'canals', name: 'Lantern Canals', blurb: 'White walls, dark roofs, arched bridges and lanterns on the water.', x: 26, y: 17, accent: '#e0564f', r: 12 })
    // Willows lean over the water wherever the bank is free.
    p.scatter({
      density: 0.3,
      on: ['cobble'],
      pick: (_roll, x, y) => {
        const wet = (gx: number, gy: number) => p.at(gx, gy) === 'canal'
        return wet(x + 1, y) || wet(x - 1, y) || wet(x, y + 1) || wet(x, y - 1) ? { kind: 'willow', x: 0, y: 0 } : null
      },
    })
    p.critter({ kind: 'boat', loop: [[0.5, 9.5], [35, 9.6]], speed: 0.45 })
    p.critter({ kind: 'boat', loop: [[17.5, 0.5], [17.5, 25.5]], speed: 0.35 })
    p.critter({ kind: 'boat', loop: [[35, 19.5], [0.5, 19.4]], speed: 0.4 })
    p.npc({
      id: 'npc-lin', name: 'Lantern Lin', role: 'Lantern maker', look: look('lanternlin', 1, 0, 2, 'bow'), speed: 1.3,
      stops: [
        { x: 28, y: 24, wait: 14, say: -1 },
        { x: 25, y: 17, wait: 8, emote: 'happy' },
        { x: 13, y: 10, wait: 10, say: -1 },
        { x: 3, y: 15, wait: 8 },
      ],
      lines: ['Every lantern on the water was folded by hand.', 'Try the dumplings, they are still warm.', 'At night the canals glow red. Come back then!', 'Mind the boats under the bridges.'],
    })
    p.npc({
      id: 'npc-bao', name: 'Auntie Bao', role: 'Dumpling seller', look: look('auntiebao', 6, 1, 1, 'chef'), speed: 1,
      stops: [
        { x: 23, y: 24, wait: 30, say: -1 },
        { x: 17, y: 14, wait: 8, emote: 'love', say: -1 },
        { x: 24, y: 20, wait: 8 },
      ],
      lines: ['Dumplings! Pork, chive, or mystery!', 'You look hungry. You always look hungry.', 'The teahouse makes the best longjing.', 'Steam keeps the cold away.'],
    })

    // ─── Detail ───
    p.prop(
      { kind: 'laundry', x: 8, y: 8, w: 1, d: 4 }, { kind: 'laundry', x: 19, y: 8, w: 1, d: 4 }, { kind: 'laundry', x: 33, y: 18, w: 1, d: 4 },
      { kind: 'festoon', x: 23, y: 16, w: 4, d: 1, variant: 'lanterns' },
      { kind: 'stall', x: 19, y: 25, color: '#e0564f', text: 'Tea' }, { kind: 'cart', x: 21, y: 25, color: '#b03a2e', text: 'Baozi' },
      { kind: 'pot', x: 5, y: 4, variant: 'bonsai' }, { kind: 'pot', x: 15, y: 4, variant: 'bonsai' }, { kind: 'pot', x: 26, y: 4, variant: 'bonsai' },
      { kind: 'crate', x: 34, y: 25 }, { kind: 'barrel', x: 35, y: 25 }, { kind: 'barrel', x: 5, y: 25 },
    )
    p.scatter(decals(['moss', 'puddle', 'moss'], 0.08, ['cobble']))
    p.npc({
      id: 'npc-bai', name: 'Poet Bai', role: 'Poet', look: look('poetbai', 7, 13, 0, 'beret'), speed: 0.9,
      stops: [{ x: 4, y: 10, wait: 14, emote: 'thinking', say: -1 }, { x: 25, y: 10, wait: 14, say: -1 }, { x: 13, y: 20, wait: 14, emote: 'love', say: -1 }],
      lines: ['“Willows lean to hear the water talk.”', 'Every bridge here has a name. This one is “Patience”.', '“Red lanterns, dark water, one small boat.”', 'Shh. A poem is landing.'],
    })
    p.npc({
      id: 'npc-wu', name: 'Boatman Wu', role: 'Boatman', look: look('boatmanwu', 5, 2, 3, 'cowboy'), speed: 1.1,
      stops: [{ x: 29, y: 8, wait: 20, say: -1 }, { x: 11, y: 18, wait: 20, emote: 'happy' }, { x: 32, y: 18, wait: 15, say: -1 }],
      lines: ['Boat ride? Mind your hat under the bridges.', 'The canal goes all the way to the sea.', 'Rowing is just walking, with water.'],
    })
  },
}
